// URL 쿼리 파라미터에서 민감 토큰 마스킹
const SENSITIVE_PARAMS = ['token', 'access_token', 'refresh_token', 'api_key', 'apikey', 'secret'];

function maskSensitiveParams(url: string): string {
  try {
    const parsed = new URL(url, window.location.origin);
    SENSITIVE_PARAMS.forEach((param) => {
      if (parsed.searchParams.has(param)) {
        parsed.searchParams.set(param, '***');
      }
    });
    // 원본이 상대경로라면 pathname+search만 반환
    return url.startsWith('http') ? parsed.toString() : `${parsed.pathname}${parsed.search}`;
  } catch {
    return url;
  }
}

export interface NetworkEntry {
  url: string;
  method: string;
  status: number;
  statusText?: string;
  duration: number;
  timestamp: string;
  responseSize?: number;
  errorInfo?: {
    code?: string;
    message?: string;
  };
}

let buffer: NetworkEntry[] = [];
let maxSize = 30;
let initialized = false;

const originalFetch = window.fetch.bind(window);

function pushEntry(entry: NetworkEntry): void {
  if (buffer.length >= maxSize) {
    buffer.shift();
  }
  buffer.push(entry);
}

export function initNetworkCollector(maxEntries = 30): void {
  if (initialized) return;
  maxSize = maxEntries;
  initialized = true;

  window.fetch = async (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
    const method = (init?.method ?? 'GET').toUpperCase();
    const rawUrl = typeof input === 'string' ? input : input instanceof URL ? input.toString() : input.url;
    const maskedUrl = maskSensitiveParams(rawUrl);
    const timestamp = new Date().toISOString();
    const startTime = performance.now();

    try {
      // Authorization 헤더는 수집하지 않음 — 그냥 요청 그대로 전달
      const response = await originalFetch(input, init);
      const duration = Math.round(performance.now() - startTime);

      const entry: NetworkEntry = {
        url: maskedUrl,
        method,
        status: response.status,
        statusText: response.statusText || undefined,
        duration,
        timestamp,
      };

      // Content-Length 헤더에서 응답 크기 추출
      const contentLength = response.headers.get('content-length');
      if (contentLength) {
        entry.responseSize = parseInt(contentLength, 10);
      }

      // 에러 응답(4xx, 5xx)인 경우 error code/message 추출
      if (response.status >= 400) {
        try {
          const cloned = response.clone();
          const body = await cloned.json();
          if (body?.error) {
            entry.errorInfo = {
              code: body.error.code ? String(body.error.code) : undefined,
              message: body.error.message ? String(body.error.message) : undefined,
            };
          }
        } catch {
          // JSON 파싱 실패 시 무시
        }
      }

      pushEntry(entry);

      return response;
    } catch (err) {
      const duration = Math.round(performance.now() - startTime);
      // 네트워크 오류는 status 0으로 기록
      pushEntry({ url: maskedUrl, method, status: 0, duration, timestamp });
      throw err;
    }
  };
}

export function getRecentNetworkRequests(): NetworkEntry[] {
  return [...buffer];
}

export function clearNetworkRequests(): void {
  buffer = [];
}
